import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, X } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  ConfirmDialog,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  MoneyInput,
  PasswordInput,
  Textarea,
} from '@mlm/ui';
import {
  cashierDiscountFromBranch,
  activateFsSlot,
  apiErrorMessage,
  listFsMemberships,
  listBranches,
  getSlotStockistBranch,
  getPosCustomFeeTypes,
  getPosPaymentMethods,
  getPosPriceMap,
  listMemberActiveSlots,
  listPosProducts,
  posCheckout,
  previewCheckout,
  searchMembers,
  searchStockists,
  verifyManagerPin,
  type ActivateFsResult,
  type ActiveSlot,
  type Branch,
  type CashierDiscountConfig,
  type MemberSearchResult,
  type PosCheckoutResult,
  type PosPaymentMethodOption,
  type PosPriceMap,
  type PosProduct,
  type PosVariant,
  type PreviewResult,
} from '../lib/api';
import { walletWord } from '../lib/coin-label';
import { prettyError } from '../lib/pos-errors';
import { useCoinLabel } from '../lib/use-coin-label';
import { useAuthStore, useHasPerm } from '../lib/auth-store';
import { Receipt } from '../components/receipt';
import { PosProductGrid } from '../components/pos-product-grid';
import {
  PriceTag,
  lineStockCap,
  lineUnitPrice,
  productPrice,
  variantPrice,
} from '../lib/pos-pricing';

type CartLine = {
  product: PosProduct;
  /** Chosen variation for a variation product; null for single-SKU lines. */
  variant: PosVariant | null;
  qty: number;
};

/** Stable cart-line identity: a product+variant pair is its own line. */
function lineKey(l: { product: { id: string }; variant: { id: string } | null }): string {
  return l.variant ? `${l.product.id}:${l.variant.id}` : l.product.id;
}

/**
 * Cart quantity control: −/+ buttons around a TYPEABLE field, so ringing up 24
 * of something isn't 23 taps.
 *
 * While focused the field holds a local draft string rather than the committed
 * number — clearing it to type "12" must not snap back to 1 on the empty
 * keystroke (the bug in the member storefront's hand-rolled steppers, which all
 * do `Number(e.target.value) || 1` on every change). The draft commits, clamped
 * to `[1, cap]`, on blur and Enter; Escape restores the committed value.
 */
function QtyStepper({
  qty,
  cap,
  onStep,
  onSet,
}: {
  qty: number;
  cap: number;
  onStep: (delta: number) => void;
  onSet: (n: number) => void;
}) {
  const [draft, setDraft] = useState<string | null>(null);

  function commit(raw: string) {
    setDraft(null);
    const n = Number.parseInt(raw, 10);
    // Empty / unparseable reverts to the committed qty; the clamp (incl. the
    // floor at 1) lives in `onSet` so − and + share it.
    if (Number.isNaN(n)) return;
    onSet(n);
  }

  return (
    <span className="inline-flex items-center gap-1">
      <button
        type="button"
        aria-label="Decrease quantity"
        className="px-1.5 rounded bg-secondary"
        onClick={() => onStep(-1)}
      >
        −
      </button>
      <MoneyInput
        integer
        aria-label="Quantity"
        className="h-6 w-12 px-1 py-0 text-center text-xs font-medium"
        value={draft ?? String(qty)}
        onChange={(e) => setDraft(e.target.value)}
        onFocus={(e) => e.currentTarget.select()}
        onBlur={(e) => commit(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur();
          else if (e.key === 'Escape') {
            // Put the committed value back BEFORE blurring so the blur handler
            // commits a no-op — `setDraft(null)` alone wouldn't have landed yet.
            e.currentTarget.value = String(qty);
            e.currentTarget.blur();
          }
        }}
      />
      <button
        type="button"
        aria-label="Increase quantity"
        className="px-1.5 rounded bg-secondary disabled:opacity-40"
        disabled={qty >= cap}
        onClick={() => onStep(+1)}
      >
        +
      </button>
    </span>
  );
}

export function PosPage({ mode = 'retail' }: { mode?: 'retail' | 'stockist' } = {}) {
  // F1 — "Sell to Stockist" mode: the member picker only surfaces stockists, the
  // page reads as a stock transfer, and checkout is blocked unless the selected
  // buyer actually owns a stockist branch. Everything else (cart, pricing,
  // payment, receipt) is the shared POS flow.
  const stockistMode = mode === 'stockist';
  const navigate = useNavigate();
  // One-shot confirmation on entering the Sell to Stockist tab — a transfer is
  // easy to mistake for a sale, so spell out the consequences up front. Shown
  // every time the tab is opened (the repo has no "don't show again" pattern).
  const [stockistAck, setStockistAck] = useState(!stockistMode);
  const user = useAuthStore((s) => s.user);
  // FS Activation is its own authority (`pos.slot.activate`) — the API has
  // always enforced it, but the button used to render for everyone, so an
  // unauthorised cashier only found out via a 403 mid-dialog.
  const canActivateFs = useHasPerm('pos.slot.activate');
  const queryClient = useQueryClient();

  // ── branch context ────────────────────────────────────────────────
  // A scoped cashier auto-uses their branch; an admin / super_admin picks.
  // Always fetched (shared cache with BranchLabel): drives the picker AND the
  // receipt company header lookup by branch id.
  const branchesQuery = useQuery({
    queryKey: ['branches'],
    queryFn: listBranches,
  });
  const [selectedBranchId, setSelectedBranchId] = useState<string | null>(user?.branchId ?? null);
  useEffect(() => {
    const first = branchesQuery.data?.[0];
    if (!selectedBranchId && first) {
      setSelectedBranchId(first.id);
    }
  }, [branchesQuery.data, selectedBranchId]);
  const branchId = user?.branchId ?? selectedBranchId;

  // ── product catalog ───────────────────────────────────────────────
  const productsQuery = useQuery({
    queryKey: ['pos-products', branchId],
    queryFn: () => listPosProducts(branchId!),
    enabled: !!branchId,
  });

  // ── member search ─────────────────────────────────────────────────
  const [memberQuery, setMemberQuery] = useState('');
  const [selectedMember, setSelectedMember] = useState<MemberSearchResult | null>(null);
  // QoL Slice 5a — explicit slot selection so a member with >1 active slot
  // can have any of them used as the buyer slot for this sale.
  const [selectedSlotId, setSelectedSlotId] = useState<string | null>(null);
  const [selectedSlotCode, setSelectedSlotCode] = useState<string | null>(null);
  const [showSlotPicker, setShowSlotPicker] = useState(false);
  // FS Activation — a self-contained "Activate Free Slot" flow, independent of
  // the cart/member selected for a sale.
  const [showFreeSlot, setShowFreeSlot] = useState(false);
  const trimmed = memberQuery.trim();
  const searchQuery = useQuery({
    queryKey: [stockistMode ? 'stockists-search' : 'members-search', trimmed],
    queryFn: () => (stockistMode ? searchStockists(trimmed) : searchMembers(trimmed)),
    enabled: trimmed.length >= 2 && !selectedMember,
    staleTime: 5_000,
  });
  // Active slots for the selected member; drives the auto-pick / picker logic.
  const slotsQuery = useQuery({
    queryKey: ['member-slots', selectedMember?.id],
    queryFn: () => listMemberActiveSlots(selectedMember!.id),
    enabled: !!selectedMember && !selectedSlotId,
  });
  // Does the SELECTED SLOT own a stockist branch? Only meaningful in stockist
  // mode — the transfer is mode-driven and keyed on the slot the cashier picked
  // (the "Sell to Stockist" search binds the owner slot directly). In normal
  // mode this stays idle so a stockist owner rings up an ordinary sale.
  const stockistQuery = useQuery({
    queryKey: ['slot-stockist', selectedSlotId],
    queryFn: () => getSlotStockistBranch(selectedSlotId!),
    enabled: stockistMode && !!selectedSlotId,
  });
  // Per-buyer discount overlay: the discounted cash price for every product /
  // variant the selected slot's tier / rank / stockist status undercuts. Keyed
  // on the MEMBER as well as the slot — a slotless buyer may still get an
  // overlay (the tenant's non-activated tier), so `null` slot is a real,
  // fetching state and two different members must not share its cache entry.
  // `stockistMode` is part of the KEY, not just the request: the retail tab and
  // the stockist tab price the same member differently, and without it the two
  // would share one cache entry and show each other's prices.
  const priceMapQuery = useQuery({
    queryKey: ['pos-price-map', branchId, selectedMember?.id ?? null, selectedSlotId, stockistMode],
    queryFn: () => getPosPriceMap(branchId!, selectedMember!.id, selectedSlotId, stockistMode),
    enabled: !!branchId && !!selectedMember,
  });
  const priceMap = selectedMember ? (priceMapQuery.data ?? null) : null;

  /**
   * QoL Slice 5a — pick a search result. Slot matches preselect the slot;
   * user matches defer to the slot list (auto-pick if exactly 1, otherwise
   * open the picker).
   */
  function pickSearchResult(r: MemberSearchResult) {
    setSelectedMember(r);
    if (r.matchType === 'slot' && r.slotId) {
      setSelectedSlotId(r.slotId);
      setSelectedSlotCode(r.slotCode);
    } else {
      setSelectedSlotId(null);
      setSelectedSlotCode(null);
    }
  }

  // Auto-select when the user has exactly 1 slot; open the picker on >1.
  useEffect(() => {
    if (!selectedMember || selectedSlotId) return;
    const slots = slotsQuery.data;
    if (!slots) return;
    if (slots.length === 1) {
      setSelectedSlotId(slots[0]!.id);
      setSelectedSlotCode(slots[0]!.slotCode);
    } else if (slots.length > 1) {
      setShowSlotPicker(true);
    }
  }, [selectedMember, selectedSlotId, slotsQuery.data]);

  function pickSlot(s: ActiveSlot) {
    setSelectedSlotId(s.id);
    setSelectedSlotCode(s.slotCode);
    setShowSlotPicker(false);
  }

  // ── cart ──────────────────────────────────────────────────────────
  const [cart, setCart] = useState<CartLine[]>([]);
  // Variation product awaiting a variant pick (opens the chooser dialog).
  const [variantPickProduct, setVariantPickProduct] = useState<PosProduct | null>(null);
  function addToCart(p: PosProduct, variant: PosVariant | null = null) {
    const key = lineKey({ product: p, variant });
    const cap = lineStockCap({ product: p, variant });
    setCart((prev) => {
      const existing = prev.find((l) => lineKey(l) === key);
      if (existing) {
        if (existing.qty + 1 > cap) return prev; // can't exceed stock
        return prev.map((l) => (lineKey(l) === key ? { ...l, qty: l.qty + 1 } : l));
      }
      if (cap < 1) return prev;
      return [...prev, { product: p, variant, qty: 1 }];
    });
  }
  /** Tile tap: single-SKU adds directly; a variation product opens the picker. */
  function onProductTap(p: PosProduct) {
    if (p.variants.length > 0) setVariantPickProduct(p);
    else addToCart(p);
  }
  function changeQty(key: string, delta: number) {
    setCart((prev) =>
      prev.flatMap((l) => {
        if (lineKey(l) !== key) return [l];
        const next = l.qty + delta;
        if (next <= 0) return [];
        if (next > lineStockCap(l)) return [l]; // cap at stock
        return [{ ...l, qty: next }];
      }),
    );
  }
  /**
   * Absolute set, for the typed quantity field. Unlike `changeQty` a typed
   * value NEVER removes the line — it floors at 1, because a mistyped `0` at a
   * busy till silently dropping a product is worse than a no-op. Use `−` at
   * qty 1, or "remove", to clear a line.
   */
  function setQty(key: string, n: number) {
    setCart((prev) =>
      prev.map((l) =>
        lineKey(l) === key ? { ...l, qty: Math.min(Math.max(n, 1), lineStockCap(l)) } : l,
      ),
    );
  }
  function removeLine(key: string) {
    setCart((prev) => prev.filter((l) => lineKey(l) !== key));
  }
  function clearCart() {
    setCart([]);
    setSelectedMember(null);
    setSelectedSlotId(null);
    setSelectedSlotCode(null);
    setMemberQuery('');
    setAmountTendered('');
    setRemarks('');
  }

  // ── totals ────────────────────────────────────────────────────────
  const goodsTotal = useMemo(
    () => cart.reduce((sum, l) => sum + lineUnitPrice(l, priceMap) * l.qty, 0),
    [cart, priceMap],
  );

  // Charges added on top of the goods (e.g. "Delivery" ₱50) — never discounted,
  // never PV-bearing; the server applies them after the manager discount.
  // Cash-denominated only; a gc/coin tender clears them below.
  //
  // The TYPE comes from the admin registry and only its code is sent — the
  // server resolves the label, which is what stops six tills spelling "Delivery"
  // six ways. The amount is still per-sale (a delivery fee varies by distance).
  const feeTypesQ = useQuery({ queryKey: ['pos-custom-fee-types'], queryFn: getPosCustomFeeTypes });
  const feeTypes = feeTypesQ.data ?? [];
  const [fees, setFees] = useState<Array<{ typeCode: string; amount: string }>>([]);
  const feeTotal = fees.reduce((sum, f) => sum + (Number(f.amount) || 0), 0);
  const feeLabelOf = (code: string) => feeTypes.find((t) => t.code === code)?.label ?? code;
  /** Types not already on another row — one line per type, so a type is offered once. */
  const availableTypes = (rowCode: string) =>
    feeTypes.filter((t) => t.code === rowCode || !fees.some((f) => f.typeCode === t.code));
  const addFeeRow = () => {
    const next = feeTypes.find((t) => !fees.some((f) => f.typeCode === t.code));
    if (next) setFees((fs) => [...fs, { typeCode: next.code, amount: '' }]);
  };
  const updateFee = (i: number, patch: Partial<{ typeCode: string; amount: string }>) =>
    setFees((fs) => fs.map((f, idx) => (idx === i ? { ...f, ...patch } : f)));
  const removeFee = (i: number) => setFees((fs) => fs.filter((_, idx) => idx !== i));

  // ── checkout ──────────────────────────────────────────────────────
  // F1 CMS: the tender options come from the admin-managed `pos_payment_methods`
  // registry (not a hardcoded list). The selected method's `tenderKind` drives
  // cash-with-change / exact (no change) / wallet-debit behavior.
  const methodsQ = useQuery({ queryKey: ['pos-payment-methods'], queryFn: getPosPaymentMethods });
  // Memoized so the `?? []` fallback isn't a fresh array on every render — it
  // is a dependency of the default-method effect below, which would otherwise
  // re-run (and re-assert the default) on every single render.
  const methods = useMemo(() => methodsQ.data ?? [], [methodsQ.data]);
  // Tenants rename the coin wallet (e.g. "UPCoin"); the till says whatever
  // they set, matching the admin and member SPAs.
  const coinLabel = useCoinLabel();
  const [selectedMethodCode, setSelectedMethodCode] = useState<string | null>(null);
  const selectedMethod = methods.find((m) => m.code === selectedMethodCode) ?? null;
  const tenderKind = selectedMethod?.tenderKind ?? 'cash';
  const [amountTendered, setAmountTendered] = useState<string>('');
  // Free-text remark stored on the order (orders.notes). Optional; shown in the
  // Sales Order detail. Cleared on every completed / cleared sale.
  const [remarks, setRemarks] = useState<string>('');
  const [receipt, setReceipt] = useState<PosCheckoutResult | null>(null);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  // QoL Slice 5a — Review step gating Charge.
  const [showReview, setShowReview] = useState(false);
  // QoL Slice 5b — order-level manager discount picked inside the Review
  // modal. `approvalChip` is non-null after a successful manager PIN verify
  // when the cap is exceeded; otherwise the discount commits without one.
  const [mgrDiscountPct, setMgrDiscountPct] = useState<string>('');
  const [mgrDiscountAmount, setMgrDiscountAmount] = useState<string>('');
  const [mgrDiscountReason, setMgrDiscountReason] = useState<string>('');
  const [mgrApprovalChip, setMgrApprovalChip] = useState<string | null>(null);
  function resetManagerDiscount() {
    setMgrDiscountPct('');
    setMgrDiscountAmount('');
    setMgrDiscountReason('');
    setMgrApprovalChip(null);
  }

  // Per-currency order total from the cart; null if any line lacks a price in
  // that currency (so the currency can't be used for this cart). A variant
  // line uses the VARIANT's flat gc/coin price (null = not sold in it).
  function currencyTotal(cur: 'gc' | 'coin'): number | null {
    let sum = 0;
    for (const l of cart) {
      const src = l.variant ?? l.product;
      const raw = cur === 'gc' ? src.priceGc : src.priceCoin;
      if (raw == null) return null;
      sum += Number(raw) * l.qty;
    }
    return sum;
  }
  const gcTotal = currencyTotal('gc');
  const coinTotal = currencyTotal('coin');
  // A wallet method debits the buyer's balance in its currency. gc/coin use the
  // flat per-currency cart total; a cash-wallet method pays the NORMAL cash total
  // from the member's cash balance (discounts + PV + comp apply as usual).
  const walletCurrency: 'cash' | 'gc' | 'coin' | null =
    tenderKind === 'wallet' ? ((selectedMethod?.currency as 'cash' | 'gc' | 'coin') ?? null) : null;
  const nonCash = tenderKind === 'wallet';
  // A fee is a peso service charge — it can't be denominated in GC/coin, so a
  // scrip tender hides the field and drops any amount already typed. A CASH
  // wallet tender still takes it (the member pays goods + fee from their cash
  // balance, exactly like drawer cash).
  const customFeeAllowed = walletCurrency !== 'gc' && walletCurrency !== 'coin';
  const effectiveCustomFee = customFeeAllowed ? feeTotal : 0;
  // The cash total the cashier collects, and what the tender guard measures
  // against — the fee MUST be in here or the change math disagrees with the
  // server, which recomputes the same sum.
  const total = (goodsTotal + effectiveCustomFee).toFixed(2);
  const payTotal =
    walletCurrency === 'gc' ? gcTotal : walletCurrency === 'coin' ? coinTotal : Number(total);
  // Only rows with a real amount are sent — an empty row the cashier added and
  // then thought better of must not become a ₱0 line on the tape.
  const chargeableFees = customFeeAllowed ? fees.filter((f) => Number(f.amount) > 0) : [];
  const customFeesPayload = chargeableFees.length > 0 ? chargeableFees : undefined;
  /** Preview lines for the review dialog + receipt, labelled from the registry. */
  const feeLines = chargeableFees.map((f) => ({
    label: feeLabelOf(f.typeCode),
    amount: f.amount,
  }));

  // A gc/coin wallet method is unusable when the cart isn't fully priced in its
  // currency. A cash-wallet method always uses the cash total, so it's never
  // priceability-disabled (the server rejects on insufficient balance instead).
  function methodDisabled(m: PosPaymentMethodOption): boolean {
    if (m.tenderKind !== 'wallet' || m.currency === 'cash') return false;
    return currencyTotal(m.currency as 'gc' | 'coin') == null;
  }

  // Default the selected method once the registry loads, and recover if the
  // current pick becomes invalid (disabled / removed).
  useEffect(() => {
    if (methods.length === 0) return;
    if (!selectedMethod) setSelectedMethodCode(methods[0]!.code);
  }, [methods, selectedMethod]);

  const checkout = useMutation({
    mutationFn: posCheckout,
    meta: { successMessage: 'Sale completed.' },
    onSuccess: (data) => {
      setReceipt(data);
      setCheckoutError(null);
      setCart([]);
      setSelectedMember(null);
      setSelectedSlotId(null);
      setSelectedSlotCode(null);
      setMemberQuery('');
      setAmountTendered('');
      setRemarks('');
      setFees([]);
      setSelectedMethodCode(null); // re-defaults to the first method via effect
      setShowReview(false);
      resetManagerDiscount();
      queryClient.invalidateQueries({ queryKey: ['pos-products', branchId] });
    },
    onError: (err: unknown) => {
      const data = (err as { response?: { data?: { error?: string; message?: string } } })?.response
        ?.data;
      setCheckoutError(data?.error ?? data?.message ?? 'checkout_failed');
    },
  });

  function buildManagerDiscount():
    | { pct?: number; amount?: number; reason?: string; approvalChip?: string }
    | undefined {
    const pct = Number(mgrDiscountPct);
    const amount = Number(mgrDiscountAmount);
    if (!pct && !amount) return undefined;
    return {
      pct: pct || undefined,
      amount: amount || undefined,
      reason: mgrDiscountReason.trim() || undefined,
      approvalChip: mgrApprovalChip ?? undefined,
    };
  }

  function onCheckout() {
    if (!branchId || !selectedMember || cart.length === 0) return;
    if (!selectedMethodCode) {
      setCheckoutError('no_payment_method');
      return;
    }
    const items = cart.map((l) => ({
      productId: l.product.id,
      variantId: l.variant?.id,
      qty: l.qty,
    }));
    const managerDiscount = buildManagerDiscount();

    if (nonCash) {
      if (payTotal == null) {
        setCheckoutError(`product_not_purchasable_in_${walletCurrency}`);
        return;
      }
      setCheckoutError(null);
      // The backend recomputes + debits the per-currency total; amountTendered
      // is informational for non-cash.
      checkout.mutate({
        branchId,
        buyerUserId: selectedMember.id,
        buyerSlotId: selectedSlotId,
        items,
        payment: {
          method: 'cash',
          currency: walletCurrency!,
          amountTendered: String(payTotal),
          paymentMethodCode: selectedMethodCode,
        },
        notes: remarks.trim() || undefined,
        managerDiscount,
        // undefined for gc/coin by construction — a scrip order can't carry a
        // peso fee, and the server rejects one outright.
        customFees: customFeesPayload,
        stockistTransfer: stockistMode,
      });
      return;
    }

    // Exact (card) tenders the total with no change; cash takes the entered bill.
    const tenderForExact = tenderKind === 'exact' ? total : amountTendered;
    if (!tenderForExact || Number(tenderForExact) < Number(total)) {
      setCheckoutError('insufficient_payment');
      return;
    }
    setCheckoutError(null);
    checkout.mutate({
      branchId,
      buyerUserId: selectedMember.id,
      buyerSlotId: selectedSlotId,
      items,
      payment: {
        method: tenderKind === 'exact' ? 'card' : 'cash',
        currency: 'cash',
        amountTendered: tenderForExact,
        paymentMethodCode: selectedMethodCode,
      },
      notes: remarks.trim() || undefined,
      managerDiscount,
      customFees: customFeesPayload,
      stockistTransfer: stockistMode,
    });
  }

  // ── render ────────────────────────────────────────────────────────
  if (!branchId) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Select a branch</CardTitle>
        </CardHeader>
        <CardContent>
          {branchesQuery.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : branchesQuery.data?.length ? (
            <BranchPicker branches={branchesQuery.data} onSelect={setSelectedBranchId} />
          ) : (
            <p className="text-sm text-muted-foreground">No branches available.</p>
          )}
        </CardContent>
      </Card>
    );
  }

  if (receipt) {
    // The branch row carries both the company header and the field-visibility
    // flags. Buyer + cashier names come off the CHECKOUT RESPONSE, not local
    // state — `onSuccess` clears `selectedMember` before this renders.
    const receiptBranch = branchesQuery.data?.find((b) => b.id === branchId);
    return (
      <Receipt
        data={{
          orderCode: receipt.order.code,
          status: receipt.order.status,
          items: receipt.items,
          subtotal: receipt.order.subtotal,
          discountTotal: receipt.order.discountTotal,
          total: receipt.order.total,
          payment: {
            method: receipt.payment.method,
            methodLabel: receipt.payment.methodLabel,
            amountTendered: receipt.payment.amountTendered,
            changeDue: receipt.payment.changeDue,
          },
          kitCodes: receipt.kitCodes,
          header: receiptBranch?.receiptHeader ?? null,
          customFeeLabel: receipt.order.customFeeLabel,
          customFeeAmount: receipt.order.customFeeAmount,
          customFees: receipt.order.customFees,
          memberName: receipt.buyerName,
          cashierName: receipt.cashierName,
          branchName: receiptBranch?.name ?? null,
          createdAt: receipt.order.createdAt,
          remarks: receipt.order.notes,
          fields: receiptBranch?.receiptFields,
        }}
        onNew={() => {
          setReceipt(null);
          clearCart();
        }}
      />
    );
  }

  return (
    <div className="grid grid-cols-12 gap-4">
      {stockistMode && (
        <div className="col-span-12 rounded-md border border-amber-300 bg-amber-50 px-3 py-2">
          <h1 className="text-sm font-semibold text-amber-900">Sell to Stockist</h1>
          <p className="text-xs text-amber-800">
            Ring up items for a stockist. On checkout the stock moves out of this branch and into
            the stockist's own branch inventory.
          </p>
        </div>
      )}
      {/* Member + branch header */}
      <div className="col-span-12 flex items-center justify-between text-sm text-muted-foreground">
        <span>
          Branch: <BranchLabel branchId={branchId} />
        </span>
        <div className="flex items-center gap-3">
          {canActivateFs && (
            <button
              onClick={() => setShowFreeSlot(true)}
              className="underline hover:text-foreground"
            >
              Activate free slot
            </button>
          )}
          {!user?.branchId && (
            <button
              onClick={() => setSelectedBranchId(null)}
              className="underline hover:text-foreground"
            >
              Switch branch
            </button>
          )}
        </div>
      </div>

      {/* Products */}
      <div className="col-span-12 lg:col-span-8 space-y-4">
        <PosProductGrid
          products={productsQuery.data}
          loading={productsQuery.isLoading}
          priceMap={priceMap}
          onTap={onProductTap}
          onScan={addToCart}
          // The cashier's first action is always picking the buyer, so don't
          // steal focus from the member search until that's done.
          autoFocus={!!selectedMember}
        />
      </div>

      {/* Cart + checkout */}
      <div className="col-span-12 lg:col-span-4 space-y-4">
        <Card>
          <CardHeader>
            <CardTitle>{stockistMode ? 'Stockist' : 'Member'}</CardTitle>
          </CardHeader>
          <CardContent>
            {selectedMember ? (
              <div className="text-sm space-y-1">
                <div className="font-medium">{selectedMember.fullName ?? selectedMember.email}</div>
                <div className="text-muted-foreground">{selectedMember.email}</div>
                {selectedMember.referralCode && (
                  <div className="text-xs text-muted-foreground">
                    Ref: {selectedMember.referralCode}
                  </div>
                )}
                {stockistMode && stockistQuery.data && (
                  <div className="mt-1 rounded-md border border-amber-300 bg-amber-50 px-2 py-1.5 text-xs text-amber-800">
                    <span className="font-medium">Stockist stock transfer</span> — these items move
                    into <span className="font-medium">{stockistQuery.data.name}</span>'s stock, not
                    a retail sale.
                  </div>
                )}
                {selectedSlotCode ? (
                  <div className="text-xs flex items-center gap-2 pt-1">
                    <Badge variant="default" className="text-[10px]">
                      slot {selectedSlotCode}
                    </Badge>
                    {(slotsQuery.data?.length ?? 0) > 1 && (
                      <button
                        className="underline text-muted-foreground"
                        onClick={() => setShowSlotPicker(true)}
                      >
                        switch slot
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="text-xs text-warning pt-1">No active slot</div>
                )}
                <button
                  className="text-xs underline mt-1"
                  onClick={() => {
                    setSelectedMember(null);
                    setSelectedSlotId(null);
                    setSelectedSlotCode(null);
                    setMemberQuery('');
                  }}
                >
                  Change member
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                {stockistMode && (
                  <p className="text-xs text-muted-foreground">
                    Search a member who owns a stockist branch. The items you ring up transfer into
                    their stock.
                  </p>
                )}
                <Input
                  placeholder={
                    stockistMode
                      ? 'Stockist name / email / phone / branch'
                      : 'Referral code / email / phone / name'
                  }
                  value={memberQuery}
                  onChange={(e) => setMemberQuery(e.target.value)}
                />
                {searchQuery.data && searchQuery.data.length > 0 && (
                  <ul className="max-h-60 overflow-auto border rounded-md divide-y">
                    {searchQuery.data.map((m, i) => (
                      <li key={`${m.matchType}-${m.id}-${m.slotId ?? i}`}>
                        <button
                          onClick={() => pickSearchResult(m)}
                          className="w-full text-left px-2 py-1.5 hover:bg-accent text-sm"
                        >
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{m.fullName ?? m.email}</span>
                            {m.matchType === 'slot' && m.slotCode && (
                              <Badge variant="default" className="text-[10px]">
                                slot {m.slotCode}
                              </Badge>
                            )}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {m.email}
                            {m.referralCode ? ` · ref ${m.referralCode}` : ''}
                          </div>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                {trimmed.length >= 2 &&
                  searchQuery.data &&
                  searchQuery.data.length === 0 &&
                  !searchQuery.isFetching && (
                    <p className="text-xs text-muted-foreground">No matches.</p>
                  )}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Cart</CardTitle>
          </CardHeader>
          <CardContent>
            {cart.length === 0 ? (
              <p className="text-sm text-muted-foreground">Empty.</p>
            ) : (
              <ul className="space-y-2 text-sm">
                {cart.map((l) => {
                  const up = l.variant
                    ? variantPrice(l.variant, priceMap)
                    : productPrice(l.product, priceMap);
                  return (
                    <li key={lineKey(l)} className="flex items-center justify-between gap-2">
                      <div className="flex-1">
                        <div className="font-medium">
                          {l.variant ? `${l.product.name} — ${l.variant.name}` : l.product.name}
                        </div>
                        <div className="text-xs text-muted-foreground flex items-center gap-1">
                          <PriceTag base={up.base} now={up.now} /> ×{' '}
                          <QtyStepper
                            qty={l.qty}
                            cap={lineStockCap(l)}
                            onStep={(d) => changeQty(lineKey(l), d)}
                            onSet={(n) => setQty(lineKey(l), n)}
                          />
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-medium">₱{(up.now * l.qty).toFixed(2)}</div>
                        <button
                          className="text-xs underline text-muted-foreground"
                          onClick={() => removeLine(lineKey(l))}
                        >
                          remove
                        </button>
                      </div>
                    </li>
                  );
                })}
                <li className="border-t pt-2 flex justify-between font-semibold">
                  <span>Total</span>
                  <span>₱{total}</span>
                </li>
              </ul>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Payment</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-4 gap-2">
              {methods.map((m) => {
                const disabled = methodDisabled(m);
                return (
                  <button
                    key={m.code}
                    disabled={disabled}
                    onClick={() => setSelectedMethodCode(m.code)}
                    className={`py-1.5 rounded-md border text-sm ${
                      selectedMethodCode === m.code
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'hover:bg-accent'
                    } ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
                    title={disabled ? `Not all items have a ${m.label} price` : undefined}
                  >
                    {m.label}
                  </button>
                );
              })}
            </div>
            {methods.length === 0 && !methodsQ.isLoading && (
              <p className="text-xs text-destructive">
                No payment methods configured. Set them up in Admin → POS payment methods.
              </p>
            )}
            {/* Ad-hoc charge. Sits ABOVE the tender field on purpose — it
                changes the amount due, so the cashier must see it before they
                key in what the customer handed over. */}
            {/* No configured types ⇒ no control at all. An empty picker would be
                a dead affordance in front of every cashier, every sale. */}
            {customFeeAllowed && feeTypes.length > 0 && (
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">
                  Custom fee <span className="text-muted-foreground/70">(optional)</span>
                </label>
                {fees.map((f, i) => (
                  <div key={i} className="flex gap-2">
                    <select
                      className="h-10 flex-1 rounded-md border bg-background px-2 text-sm"
                      value={f.typeCode}
                      onChange={(e) => updateFee(i, { typeCode: e.target.value })}
                    >
                      {/* Only types not already used on another row, plus this
                          row's own — otherwise the same fee could be added twice
                          and the server would reject the sale at the till. */}
                      {availableTypes(f.typeCode).map((t) => (
                        <option key={t.code} value={t.code}>
                          {t.label}
                        </option>
                      ))}
                    </select>
                    <Input
                      type="number"
                      inputMode="decimal"
                      min="0"
                      value={f.amount}
                      onChange={(e) => updateFee(i, { amount: e.target.value })}
                      placeholder="0.00"
                      className="w-28"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeFee(i)}
                      aria-label={`Remove ${feeLabelOf(f.typeCode)}`}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addFeeRow}
                  disabled={fees.length >= feeTypes.length}
                  title={fees.length >= feeTypes.length ? 'All fee types added' : undefined}
                >
                  <Plus className="h-4 w-4" /> Add fee
                </Button>
                {fees.some((f) => Number(f.amount) < 0) && (
                  <p className="text-xs text-destructive">
                    A fee can&apos;t be negative. Use the cashier discount to reduce a sale.
                  </p>
                )}
              </div>
            )}
            {tenderKind === 'cash' && (
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground" htmlFor="tendered">
                  Amount tendered
                </label>
                <Input
                  id="tendered"
                  type="number"
                  inputMode="decimal"
                  value={amountTendered}
                  onChange={(e) => setAmountTendered(e.target.value)}
                  placeholder={total}
                />
              </div>
            )}
            {nonCash && (
              <p className="text-xs text-muted-foreground">
                {payTotal != null
                  ? `Debits ${payTotal.toLocaleString()} ${walletWord(walletCurrency ?? 'coin', coinLabel)} from the member's wallet.`
                  : `Debits the member's ${walletWord(walletCurrency ?? 'coin', coinLabel)} wallet.`}
              </p>
            )}
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground" htmlFor="remarks">
                Remarks <span className="text-muted-foreground/70">(optional)</span>
              </label>
              <Textarea
                id="remarks"
                rows={2}
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                placeholder="Note for this sale (e.g. reserved for pickup, special request)"
                maxLength={500}
              />
            </div>
            {checkoutError && (
              <p className="text-xs text-destructive">{prettyError(checkoutError, coinLabel)}</p>
            )}
            {stockistMode && selectedMember && !stockistQuery.data && !stockistQuery.isLoading && (
              <p className="text-xs text-destructive">
                This member does not own a stockist branch — pick a stockist to transfer stock.
              </p>
            )}
            <Button
              className="w-full"
              disabled={
                cart.length === 0 ||
                !selectedMember ||
                checkout.isPending ||
                !selectedMethodCode ||
                (tenderKind === 'cash' && !amountTendered) ||
                (nonCash && payTotal == null) ||
                // A labelled fee or no fee — never an unexplained charge.
                (customFeeAllowed && fees.some((f) => Number(f.amount) < 0)) ||
                (stockistMode && !stockistQuery.data)
              }
              onClick={() => setShowReview(true)}
            >
              Review…
            </Button>
            {cart.length > 0 && (
              <button
                className="w-full text-xs underline text-muted-foreground"
                onClick={clearCart}
              >
                Clear sale
              </button>
            )}
          </CardContent>
        </Card>
      </div>

      <SlotPickerDialog
        open={showSlotPicker}
        slots={slotsQuery.data ?? []}
        memberLabel={selectedMember?.fullName ?? selectedMember?.email ?? ''}
        onPick={pickSlot}
        onClose={() => setShowSlotPicker(false)}
      />

      <VariantPickerDialog
        product={variantPickProduct}
        priceMap={priceMap}
        onPick={(p, v) => {
          addToCart(p, v);
          setVariantPickProduct(null);
        }}
        onClose={() => setVariantPickProduct(null)}
      />

      <ReviewDialog
        open={showReview}
        branchId={branchId}
        branch={branchesQuery.data?.find((b) => b.id === branchId) ?? null}
        member={selectedMember}
        slotId={selectedSlotId}
        slotCode={selectedSlotCode}
        items={cart.map((l) => ({ productId: l.product.id, variantId: l.variant?.id, qty: l.qty }))}
        stockistTransfer={stockistMode}
        paymentCurrency={nonCash ? (walletCurrency ?? 'cash') : 'cash'}
        amountTendered={tenderKind === 'exact' ? total : amountTendered}
        customFees={feeLines}
        payWith={
          tenderKind === 'cash'
            ? 'cash'
            : tenderKind === 'exact'
              ? 'card'
              : (walletCurrency ?? 'cash')
        }
        managerDiscount={{
          pct: mgrDiscountPct,
          amount: mgrDiscountAmount,
          reason: mgrDiscountReason,
          approvalChip: mgrApprovalChip,
        }}
        setManagerDiscount={{
          setPct: setMgrDiscountPct,
          setAmount: setMgrDiscountAmount,
          setReason: setMgrDiscountReason,
          setApprovalChip: setMgrApprovalChip,
        }}
        onClose={() => {
          setShowReview(false);
        }}
        onConfirm={onCheckout}
        confirming={checkout.isPending}
        confirmError={checkoutError ? prettyError(checkoutError, coinLabel) : null}
      />

      <FreeSlotDialog open={showFreeSlot && canActivateFs} onClose={() => setShowFreeSlot(false)} />

      <ConfirmDialog
        open={stockistMode && !stockistAck}
        onOpenChange={(o) => {
          if (!o) navigate('/');
        }}
        title="Sell to Stockist — this is a transfer, not a sale"
        confirmLabel="I understand — continue"
        cancelLabel="Go to Checkout"
        description={
          <>
            On checkout the stock moves out of this branch and into the stockist's own branch
            inventory. This is a <strong>restock</strong>: no PV is earned, no commissions are paid
            to anyone, and no Code+PIN is issued. The stockist pays for the goods now — the PV and
            commissions happen later, when they sell these items onward. To ring up an ordinary
            PV-earning sale, use the <strong>Checkout</strong> tab instead.
          </>
        }
        onConfirm={() => setStockistAck(true)}
      />
    </div>
  );
}

/**
 * FS Activation via Cashier — directly create + activate a FREE (FS) slot for a
 * member (no order/payment). Self-contained: its own member search + FS-eligible
 * tier picker. Only tiers an admin flagged FS-eligible are listed; uplines earn
 * nothing on a free entry.
 */
// Server codes worth a human sentence at the counter (others show as-is).
const FS_ERROR_COPY: Record<string, string> = {
  recruit_membership_not_allowed:
    "The sponsor's membership is not allowed to recruit this package.",
  membership_binary_locked:
    'This package cannot take a binary position — clear the anchor/side and try again.',
};

function FreeSlotDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [term, setTerm] = useState('');
  const [member, setMember] = useState<MemberSearchResult | null>(null);
  const [membershipId, setMembershipId] = useState('');
  const [sponsorCode, setSponsorCode] = useState('');
  const [result, setResult] = useState<ActivateFsResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const trimmed = term.trim();
  const search = useQuery({
    queryKey: ['fs-members-search', trimmed],
    queryFn: () => searchMembers(trimmed),
    enabled: open && trimmed.length >= 2 && !member,
    staleTime: 5_000,
  });
  const memberships = useQuery({
    queryKey: ['fs-memberships'],
    queryFn: listFsMemberships,
    enabled: open,
  });

  const activate = useMutation({
    mutationFn: activateFsSlot,
    onSuccess: (r) => {
      setResult(r);
      setError(null);
    },
    onError: (e) => {
      const msg = apiErrorMessage(e, 'Activation failed');
      setError(FS_ERROR_COPY[msg] ?? msg);
    },
  });

  function reset() {
    setTerm('');
    setMember(null);
    setMembershipId('');
    setSponsorCode('');
    setResult(null);
    setError(null);
    activate.reset();
  }
  function close() {
    reset();
    onClose();
  }

  // De-dupe search rows (smart search returns user + slot matches for one person).
  const memberResults = Array.from(new Map((search.data ?? []).map((r) => [r.id, r])).values());

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) close();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Activate free (FS) slot</DialogTitle>
        </DialogHeader>

        {result ? (
          <div className="space-y-3 text-sm">
            <div className="rounded-md border border-emerald-300 bg-emerald-50 px-3 py-2 text-emerald-900">
              Free slot <span className="font-mono font-semibold">{result.slotCode}</span> created
              and activated{result.membershipCode ? ` (${result.membershipCode})` : ''}.
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={reset}>
                Activate another
              </Button>
              <Button onClick={close}>Done</Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-3 text-sm">
            <p className="text-xs text-muted-foreground">
              Creates a free (FS) slot for a member — no order or payment. Only FS-eligible tiers
              are listed. Uplines earn nothing on a free entry.
            </p>

            {member ? (
              <div className="flex items-center justify-between rounded-md border px-3 py-2">
                <div>
                  <div className="font-medium">{member.fullName ?? member.email}</div>
                  <div className="text-xs text-muted-foreground">{member.email}</div>
                </div>
                <button className="text-xs underline" onClick={() => setMember(null)}>
                  Change
                </button>
              </div>
            ) : (
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Member</label>
                <Input
                  value={term}
                  onChange={(e) => setTerm(e.target.value)}
                  placeholder="Search name / email / phone…"
                />
                {trimmed.length >= 2 && (
                  <div className="max-h-40 overflow-auto rounded-md border divide-y">
                    {search.isLoading ? (
                      <div className="px-3 py-2 text-xs text-muted-foreground">Searching…</div>
                    ) : memberResults.length === 0 ? (
                      <div className="px-3 py-2 text-xs text-muted-foreground">No matches.</div>
                    ) : (
                      memberResults.map((r) => (
                        <button
                          key={r.id}
                          onClick={() => {
                            setMember(r);
                            setTerm('');
                          }}
                          className="block w-full text-left px-3 py-2 hover:bg-accent"
                        >
                          <div className="font-medium">{r.fullName ?? r.email}</div>
                          <div className="text-xs text-muted-foreground">{r.email}</div>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">FS-eligible tier</label>
              <select
                className="w-full h-9 rounded-md border bg-background px-2 text-sm"
                value={membershipId}
                onChange={(e) => setMembershipId(e.target.value)}
              >
                <option value="">Select a tier…</option>
                {(memberships.data ?? []).map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name} ({m.code})
                  </option>
                ))}
              </select>
              {memberships.data && memberships.data.length === 0 && (
                <p className="text-[10px] text-warning">
                  No FS-eligible tiers. Mark a membership FS-eligible in Admin → Memberships.
                </p>
              )}
            </div>

            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Sponsor (optional)</label>
              <Input
                value={sponsorCode}
                onChange={(e) => setSponsorCode(e.target.value)}
                placeholder="Referral or slot code — blank = default"
              />
            </div>

            {error && <p className="text-sm text-destructive">{error}</p>}

            <DialogFooter>
              <Button variant="outline" onClick={close}>
                Cancel
              </Button>
              <Button
                disabled={!member || !membershipId || activate.isPending}
                onClick={() => {
                  setError(null);
                  activate.mutate({
                    memberId: member!.id,
                    membershipId,
                    sponsorCode: sponsorCode.trim() || undefined,
                  });
                }}
              >
                {activate.isPending ? 'Activating…' : 'Activate free slot'}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * Variation chooser: a variation product sells per-variant, so tapping its
 * tile opens this picker; the chosen variant becomes its own cart line.
 */
function VariantPickerDialog({
  product,
  priceMap,
  onPick,
  onClose,
}: {
  product: PosProduct | null;
  priceMap: PosPriceMap | null;
  onPick: (p: PosProduct, v: PosVariant) => void;
  onClose: () => void;
}) {
  return (
    <Dialog
      open={!!product}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Choose a variation</DialogTitle>
        </DialogHeader>
        {product && (
          <>
            <p className="text-sm text-muted-foreground">{product.name}</p>
            <ul className="divide-y border rounded-md">
              {product.variants.map((v) => (
                <li key={v.id}>
                  <button
                    className="w-full text-left px-3 py-2 hover:bg-accent flex items-center justify-between disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                    disabled={!v.inStock}
                    onClick={() => onPick(product, v)}
                  >
                    <div>
                      <div className="font-medium text-sm">{v.name}</div>
                      <div className="text-xs text-muted-foreground">
                        <PriceTag
                          base={variantPrice(v, priceMap).base}
                          now={variantPrice(v, priceMap).now}
                        />
                      </div>
                    </div>
                    <span
                      className={`text-xs ${
                        !v.inStock
                          ? 'text-destructive'
                          : v.stockQty < 5
                            ? 'text-warning'
                            : 'text-muted-foreground'
                      }`}
                    >
                      {v.inStock ? `${v.stockQty} in stock` : 'out of stock'}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function SlotPickerDialog({
  open,
  slots,
  memberLabel,
  onPick,
  onClose,
}: {
  open: boolean;
  slots: ActiveSlot[];
  memberLabel: string;
  onPick: (s: ActiveSlot) => void;
  onClose: () => void;
}) {
  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Pick a slot</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          {memberLabel} has {slots.length} active slots. Choose the one to use for this sale.
        </p>
        <ul className="divide-y border rounded-md">
          {slots.map((s) => (
            <li key={s.id}>
              <button
                className="w-full text-left px-3 py-2 hover:bg-accent flex items-center justify-between"
                onClick={() => onPick(s)}
              >
                <div>
                  <div className="font-mono text-sm">{s.slotCode}</div>
                  <div className="text-xs text-muted-foreground">{s.membershipName}</div>
                </div>
                {s.activatedAt && (
                  <span className="text-xs text-muted-foreground">
                    activated {new Date(s.activatedAt).toLocaleDateString()}
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      </DialogContent>
    </Dialog>
  );
}

function ReviewDialog({
  open,
  branchId,
  branch,
  member,
  slotId,
  slotCode,
  items,
  stockistTransfer,
  paymentCurrency,
  amountTendered,
  customFees,
  payWith,
  managerDiscount,
  setManagerDiscount,
  onClose,
  onConfirm,
  confirming,
  confirmError,
}: {
  open: boolean;
  branchId: string | null;
  branch: Branch | null;
  member: MemberSearchResult | null;
  slotId: string | null;
  slotCode: string | null;
  items: Array<{ productId: string; variantId?: string; qty: number }>;
  /** "Sell to Stockist" mode — prices against the destination stockist branch. */
  stockistTransfer: boolean;
  paymentCurrency: 'cash' | 'gc' | 'coin';
  amountTendered: string;
  /** Ad-hoc charge added on top; stacked locally like the manager discount. */
  /** Preview lines, already labelled from the registry. */
  customFees: Array<{ label: string; amount: string }>;
  payWith: 'cash' | 'card' | 'gc' | 'coin';
  managerDiscount: { pct: string; amount: string; reason: string; approvalChip: string | null };
  setManagerDiscount: {
    setPct: (v: string) => void;
    setAmount: (v: string) => void;
    setReason: (v: string) => void;
    setApprovalChip: (v: string | null) => void;
  };
  onClose: () => void;
  onConfirm: () => void;
  confirming: boolean;
  confirmError: string | null;
}) {
  // Cashier-discount policy lives on the BRANCH (per-store override authority).
  // The Branch row is already in cache from listBranches() so we read it
  // synchronously — no extra fetch, no perm gate.
  const cashierCfg: CashierDiscountConfig = cashierDiscountFromBranch(branch);

  const mdPctNum = Number(managerDiscount.pct) || 0;
  const mdAmountNum = Number(managerDiscount.amount) || 0;
  const aboveCap =
    cashierCfg.enabled &&
    (mdPctNum > cashierCfg.max_pct_without_approval ||
      mdAmountNum > cashierCfg.max_amount_without_approval);

  // The preview only depends on what the server actually computes — the
  // line stack (membership/rank/stockist). The manager discount is a
  // trivial subtract-from-goods we do client-side, so typing in the
  // discount fields doesn't trigger a network round-trip + spinner on
  // every keystroke. The server re-validates the cap at /pos/checkout.
  const previewQ = useQuery({
    queryKey: [
      'pos-preview',
      branchId,
      member?.id,
      slotId,
      paymentCurrency,
      stockistTransfer,
      JSON.stringify(items),
    ],
    queryFn: () =>
      previewCheckout({
        branchId: branchId!,
        buyerUserId: member!.id,
        buyerSlotId: slotId,
        items,
        paymentCurrency,
        stockistTransfer,
      }),
    enabled: open && !!branchId && !!member && items.length > 0,
  });

  const serverPreview: PreviewResult | undefined = previewQ.data;
  const coinLabel = useCoinLabel();
  const currencyLabel = paymentCurrency === 'cash' ? '₱' : walletWord(paymentCurrency, coinLabel);
  const fmt = (v: string) =>
    paymentCurrency === 'cash'
      ? `₱${Number(v).toFixed(2)}`
      : `${Number(v).toFixed(2)} ${currencyLabel}`;

  // Goods after line discounts (what the server returns as `total` when no
  // manager discount is passed). Manager discount stacks on top.
  const goodsTotal = serverPreview ? Number(serverPreview.total) : 0;
  const computedManagerAmount = Math.max(
    0,
    Math.min(goodsTotal, (goodsTotal * mdPctNum) / 100 + mdAmountNum),
  );
  // The fee lands AFTER the discount, mirroring the server's math exactly —
  // added on top so it can never be discounted away.
  const customFeeNum = customFees.reduce((sum, f) => sum + (Number(f.amount) || 0), 0);
  const finalTotal = Math.max(0, goodsTotal - computedManagerAmount) + customFeeNum;
  // Shape the preview UI consumes — server fields + locally-computed manager
  // discount + final total. Same shape as the API return so the JSX below
  // doesn't need to change.
  const preview = serverPreview
    ? {
        ...serverPreview,
        managerDiscountAmount: computedManagerAmount.toFixed(4),
        total: finalTotal.toFixed(4),
      }
    : undefined;

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o && !confirming) onClose();
      }}
    >
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Review order</DialogTitle>
        </DialogHeader>

        {!member ? null : !preview ? (
          previewQ.isError ? (
            <p className="text-sm text-destructive">Could not compute preview.</p>
          ) : (
            <p className="text-sm text-muted-foreground">Computing totals…</p>
          )
        ) : (
          <div className="space-y-3 text-sm">
            <div className="text-xs text-muted-foreground">
              {member.fullName ?? member.email}
              {slotCode ? ` · slot ${slotCode}` : ' · (no active slot)'}
            </div>

            <table className="w-full">
              <thead className="text-left text-[10px] uppercase tracking-wide text-muted-foreground border-b">
                <tr>
                  <th className="py-1.5 pr-2">Item</th>
                  <th className="py-1.5 pr-2 text-right">Qty</th>
                  <th className="py-1.5 pr-2 text-right">Unit</th>
                  <th className="py-1.5 text-right">Line</th>
                </tr>
              </thead>
              <tbody>
                {preview.lines.map((l) => (
                  <tr key={l.productId} className="border-b last:border-0">
                    <td className="py-1.5 pr-2">
                      <div>{l.productName}</div>
                      <div className="text-[10px] font-mono text-muted-foreground">
                        {l.productSku}
                      </div>
                    </td>
                    <td className="py-1.5 pr-2 text-right">× {l.qty}</td>
                    <td className="py-1.5 pr-2 text-right">
                      {fmt(l.unitPrice)}
                      {Number(l.appliedDiscountPct) > 0 && (
                        <div className="text-[10px] text-success">
                          −{Number(l.appliedDiscountPct).toFixed(2)}%
                        </div>
                      )}
                    </td>
                    <td className="py-1.5 text-right font-medium">
                      {fmt((Number(l.unitPrice) * l.qty).toFixed(4))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="rounded-md border bg-muted/30 p-3 space-y-1">
              <div className="flex justify-between">
                <span>Subtotal</span>
                <span>{fmt(preview.subtotal)}</span>
              </div>
              {Number(preview.discountTotal) > 0 && (
                <div className="flex justify-between text-success">
                  <span>Discount</span>
                  <span>−{fmt(preview.discountTotal)}</span>
                </div>
              )}
              {paymentCurrency === 'cash' && Number(preview.discountTotal) > 0 && (
                <div className="text-[10px] text-muted-foreground space-y-0.5 pt-1">
                  <div>Membership / rank / stockist pricing applied per-line where set.</div>
                </div>
              )}
              {Number(preview.managerDiscountAmount) > 0 && (
                <div className="flex justify-between text-success border-t pt-1 mt-1">
                  <span>Cashier discount</span>
                  <span>−{fmt(preview.managerDiscountAmount)}</span>
                </div>
              )}
              {customFees.length > 0 && (
                <div className="border-t pt-1 mt-1">
                  {customFees.map((f, i) => (
                    <div key={i} className="flex justify-between">
                      <span>{f.label}</span>
                      <span>+{fmt(f.amount)}</span>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex justify-between font-semibold border-t pt-1 mt-1">
                <span>Total</span>
                <span>{fmt(preview.total)}</span>
              </div>
              {payWith === 'cash' && amountTendered && (
                <div className="flex justify-between text-xs pt-1">
                  <span>Tendered</span>
                  <span>{fmt(amountTendered)}</span>
                </div>
              )}
            </div>

            {paymentCurrency === 'cash' && cashierCfg.enabled && (
              <ManagerDiscountSection
                cfg={cashierCfg}
                pct={managerDiscount.pct}
                amount={managerDiscount.amount}
                reason={managerDiscount.reason}
                approvalChip={managerDiscount.approvalChip}
                setPct={setManagerDiscount.setPct}
                setAmount={setManagerDiscount.setAmount}
                setReason={setManagerDiscount.setReason}
                setApprovalChip={setManagerDiscount.setApprovalChip}
                aboveCap={aboveCap}
              />
            )}

            {confirmError && <p className="text-sm text-destructive">{confirmError}</p>}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={confirming}>
            Back
          </Button>
          <Button
            onClick={onConfirm}
            disabled={confirming || !preview || (aboveCap && !managerDiscount.approvalChip)}
          >
            {confirming ? 'Charging…' : `Charge ${preview ? fmt(preview.total) : ''}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ManagerDiscountSection({
  cfg,
  pct,
  amount,
  reason,
  approvalChip,
  setPct,
  setAmount,
  setReason,
  setApprovalChip,
  aboveCap,
}: {
  cfg: CashierDiscountConfig;
  pct: string;
  amount: string;
  reason: string;
  approvalChip: string | null;
  setPct: (v: string) => void;
  setAmount: (v: string) => void;
  setReason: (v: string) => void;
  setApprovalChip: (v: string | null) => void;
  aboveCap: boolean;
}) {
  const [pinMode, setPinMode] = useState(false);
  const [managerEmail, setManagerEmail] = useState('');
  const [pin, setPin] = useState('');
  const [pinError, setPinError] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(false);

  async function verify() {
    setPinError(null);
    setVerifying(true);
    try {
      const { approvalChip: chip } = await verifyManagerPin({ managerEmail, pin });
      setApprovalChip(chip);
      setPinMode(false);
      setPin('');
    } catch (err) {
      const code = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setPinError(
        code === 'invalid_pin' || code === 'manager_not_found' || code === 'manager_missing_pin'
          ? 'PIN or manager email is incorrect.'
          : code === 'manager_missing_perm'
            ? 'That user is not authorized to approve discounts.'
            : 'Could not verify PIN.',
      );
    } finally {
      setVerifying(false);
    }
  }

  return (
    <div className="rounded-md border p-3 space-y-2 bg-muted/10">
      <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        Cashier discount
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1">
          <label className="text-[10px] text-muted-foreground">Percent</label>
          <Input
            type="number"
            inputMode="decimal"
            value={pct}
            onChange={(e) => {
              setPct(e.target.value);
              setApprovalChip(null);
            }}
            placeholder="0"
            min={0}
            max={100}
          />
        </div>
        <div className="space-y-1">
          <label className="text-[10px] text-muted-foreground">Amount (₱)</label>
          <Input
            type="number"
            inputMode="decimal"
            value={amount}
            onChange={(e) => {
              setAmount(e.target.value);
              setApprovalChip(null);
            }}
            placeholder="0"
            min={0}
          />
        </div>
      </div>
      <div className="space-y-1">
        <label className="text-[10px] text-muted-foreground">Reason</label>
        <Input
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. damaged box, regular customer"
        />
      </div>

      <div className="text-[10px] text-muted-foreground">
        Cap without approval: {cfg.max_pct_without_approval}% / ₱
        {cfg.max_amount_without_approval.toFixed(2)}.
      </div>

      {aboveCap &&
        (approvalChip ? (
          <div className="text-xs text-success">✓ Manager approval recorded.</div>
        ) : !pinMode ? (
          <Button size="sm" variant="outline" onClick={() => setPinMode(true)}>
            Above cap: manager PIN required
          </Button>
        ) : (
          <div className="space-y-2 border-t pt-2">
            <div className="space-y-1">
              <label className="text-[10px] text-muted-foreground">Manager email</label>
              <Input
                value={managerEmail}
                onChange={(e) => setManagerEmail(e.target.value)}
                placeholder="manager@example.com"
                autoFocus
              />
            </div>
            <div className="space-y-1">
              <label className="text-[10px] text-muted-foreground">PIN</label>
              <PasswordInput
                inputMode="numeric"
                value={pin}
                onChange={(e) => setPin(e.target.value)}
                placeholder="4-8 digits"
              />
            </div>
            {pinError && <p className="text-xs text-destructive">{pinError}</p>}
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setPinMode(false);
                  setPin('');
                  setPinError(null);
                }}
                disabled={verifying}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={() => void verify()}
                disabled={!managerEmail || !pin || verifying}
              >
                {verifying ? 'Verifying…' : 'Verify PIN'}
              </Button>
            </div>
          </div>
        ))}
    </div>
  );
}

function BranchPicker({
  branches,
  onSelect,
}: {
  branches: Branch[];
  onSelect: (id: string) => void;
}) {
  return (
    <ul className="space-y-2">
      {branches.map((b) => (
        <li key={b.id}>
          <button
            onClick={() => onSelect(b.id)}
            className="w-full text-left p-3 border rounded-md hover:bg-accent"
          >
            <div className="font-medium">{b.name}</div>
            <div className="text-xs text-muted-foreground">{b.code}</div>
          </button>
        </li>
      ))}
    </ul>
  );
}

function BranchLabel({ branchId }: { branchId: string }) {
  const branchesQuery = useQuery({
    queryKey: ['branches'],
    queryFn: listBranches,
  });
  const branch = branchesQuery.data?.find((b) => b.id === branchId);
  return <span className="font-medium text-foreground">{branch?.name ?? branchId}</span>;
}
